import { useEffect, useState, useCallback } from "react";
import { Download, Receipt, Ban, CreditCard, History } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/pages/Portal";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";

const STATUS_COLOR = { unpaid: "bg-amber-100 text-amber-700", partially_paid: "bg-blue-100 text-blue-700", paid: "bg-green-100 text-green-700", overdue: "bg-red-100 text-red-700", cancelled: "bg-slate-200 text-slate-600" };
const METHODS = ["bank_transfer", "upi", "cash", "cheque", "card"];

export default function Billing() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [list, setList] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [quoteOptions, setQuoteOptions] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [summary, setSummary] = useState(null);
  const [billForm, setBillForm] = useState({ bill_label: "1st R.A. Bill", due_date: "", po_reference: "", gst_mode: "exclusive", tax_rate: 18 });
  const [billedQty, setBilledQty] = useState({});
  const [overrideReason, setOverrideReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [payTarget, setPayTarget] = useState(null);
  const [payForm, setPayForm] = useState({ amount: "", method: "upi", note: "" });
  const [historyTarget, setHistoryTarget] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [cancelTarget, setCancelTarget] = useState(null);

  const load = useCallback(() => { api.get("/billing-invoices").then((r) => setList(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/quotations").then((r) => setQuoteOptions(r.data.filter((q) => q.status === "sent"))).catch((e) => toast.error(apiErr(e)));
    const prefill = sessionStorage.getItem("sf_billing_prefill");
    if (prefill) { setSelectedGroup(prefill); setCreateOpen(true); sessionStorage.removeItem("sf_billing_prefill"); }
  }, [isAdmin]);

  useEffect(() => {
    if (!selectedGroup) { setSummary(null); return; }
    api.get(`/quotations/group/${selectedGroup}/billing-summary`).then((r) => {
      setSummary(r.data);
      const qty = {};
      r.data.sections.forEach((s, si) => s.items.forEach((it, ii) => { qty[`${si}:${ii}`] = it.remaining_qty; }));
      setBilledQty(qty);
    }).catch((e) => toast.error(apiErr(e)));
  }, [selectedGroup]);

  const createBill = async () => {
    if (!summary) return toast.error("Select a quotation first");
    const sections = summary.sections.map((s, si) => ({
      name: s.name,
      items: s.items
        .map((it, ii) => ({
          item_id: it.item_id, name: it.name, unit: it.unit, hsn_code: it.hsn_code, rate: it.rate,
          quoted_qty: it.qty, billed_qty: parseFloat(billedQty[`${si}:${ii}`]) || 0,
        }))
        .filter((it) => it.billed_qty > 0),
    })).filter((s) => s.items.length);
    if (!sections.length) return toast.error("Enter a billed quantity for at least one item");
    setSaving(true);
    try {
      await api.post("/billing-invoices", {
        quote_group_id: selectedGroup, bill_label: billForm.bill_label, customer_type: summary.customer_type,
        customer_ref_id: summary.customer_ref_id, customer_name: summary.customer_name, customer_address: summary.customer_address,
        customer_gstin: summary.customer_gstin, customer_phone: summary.customer_phone, customer_email: summary.customer_email,
        place_of_supply: summary.place_of_supply, sections, gst_mode: billForm.gst_mode, tax_rate: parseFloat(billForm.tax_rate) || 18,
        po_reference: billForm.po_reference, due_date: billForm.due_date, override_reason: overrideReason,
      });
      toast.success("Bill created");
      setCreateOpen(false); setSelectedGroup(""); setSummary(null); setOverrideReason("");
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const download = async (id) => {
    try {
      const res = await api.get(`/billing-invoices/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) { toast.error("Could not open PDF"); }
  };

  const recordPayment = async () => {
    if (!payTarget || !payForm.amount) return;
    try {
      await api.post(`/billing-invoices/${payTarget.id}/payments`, { amount: parseFloat(payForm.amount), method: payForm.method, note: payForm.note });
      toast.success("Payment recorded"); setPayTarget(null); setPayForm({ amount: "", method: "upi", note: "" }); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const openHistory = async (inv) => {
    setHistoryTarget(inv);
    try { const r = await api.get(`/billing-invoices/${inv.id}/payments`); setHistoryRows(r.data); } catch (e) { setHistoryRows([]); }
  };

  const confirmCancel = async () => {
    try { await api.patch(`/billing-invoices/${cancelTarget.id}/cancel`); toast.success("Invoice cancelled, quantity restored to quotation"); setCancelTarget(null); load(); } catch (e) { toast.error(apiErr(e)); }
  };

  const downloadReceipt = async (paymentId) => {
    try {
      const res = await api.get(`/payments/${paymentId}/receipt/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) { toast.error("Could not open receipt"); }
  };

  return (
    <div className="p-8">
      <PageHeader title="Billing & Payments" subtitle="Running / Partial Invoices">
        {isAdmin && <Button onClick={() => setCreateOpen(true)} className="rounded-sm bg-primary hover:bg-primary/90 gap-2" data-testid="new-bill-btn"><Receipt size={16} /> Create Bill</Button>}
      </PageHeader>

      <div className="bg-card border border-border rounded-sm overflow-hidden overflow-x-auto">
        <Table className="min-w-[960px]">
          <TableHeader><TableRow className="bg-muted/50">
            <TableHead>Number</TableHead><TableHead>Bill Label</TableHead>{isAdmin && <TableHead>Customer</TableHead>}
            <TableHead>Total</TableHead><TableHead>Paid</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {list.map((inv) => (
              <TableRow key={inv.id} data-testid={`bill-row-${inv.id}`}>
                <TableCell className="font-mono text-xs">{inv.invoice_number}</TableCell>
                <TableCell>{inv.bill_label}</TableCell>
                {isAdmin && <TableCell>{inv.customer_name}</TableCell>}
                <TableCell className="font-semibold">₹{(inv.grand_total || 0).toLocaleString("en-IN")}</TableCell>
                <TableCell>₹{(inv.paid_amount || 0).toLocaleString("en-IN")}</TableCell>
                <TableCell><Badge className={`rounded-sm capitalize ${STATUS_COLOR[inv.display_status] || ""}`} data-testid={`bill-status-${inv.id}`}>{(inv.display_status || "").replace("_", " ")}</Badge></TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1 flex-wrap">
                    {isAdmin && inv.status !== "cancelled" && inv.payment_status !== "paid" && (
                      <Button size="sm" variant="outline" onClick={() => setPayTarget(inv)} className="rounded-sm gap-1" data-testid={`record-payment-${inv.id}`}><CreditCard size={14} /> Record Payment</Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => openHistory(inv)} className="rounded-sm" data-testid={`payment-history-${inv.id}`}><History size={14} /></Button>
                    <Button size="sm" variant="outline" onClick={() => download(inv.id)} className="rounded-sm gap-1" data-testid={`bill-download-${inv.id}`}><Download size={14} /></Button>
                    {isAdmin && inv.status !== "cancelled" && !inv.paid_amount && (
                      <Button size="sm" variant="outline" onClick={() => setCancelTarget(inv)} className="rounded-sm text-red-600 border-red-200" data-testid={`bill-cancel-${inv.id}`}><Ban size={14} /></Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {list.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-12 text-muted-foreground">No invoices yet. Create one from an accepted quotation.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={createOpen} onOpenChange={(o) => { setCreateOpen(o); if (!o) { setSelectedGroup(""); setSummary(null); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Create Bill / Running Invoice</DialogTitle>
            <DialogDescription>Pick a quotation, then set the actual billed quantity per item — defaults to the remaining unbilled quantity.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase">Quotation</Label>
              <Select value={selectedGroup} onValueChange={setSelectedGroup}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="bill-quote-select"><SelectValue placeholder="Select a sent quotation…" /></SelectTrigger>
                <SelectContent>{quoteOptions.map((q) => <SelectItem key={q.quote_group_id} value={q.quote_group_id}>{q.quotation_number} — {q.customer_name} (₹{q.grand_total.toLocaleString("en-IN")})</SelectItem>)}</SelectContent>
              </Select>
            </div>

            {summary && (
              <>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div><Label className="text-xs uppercase">Bill Label</Label><Input value={billForm.bill_label} onChange={(e) => setBillForm({ ...billForm, bill_label: e.target.value })} placeholder="e.g. 1st R.A. Bill, Final Bill" className="rounded-sm mt-1" data-testid="bill-label-input" /></div>
                  <div><Label className="text-xs uppercase">Due Date</Label><Input type="date" value={billForm.due_date} onChange={(e) => setBillForm({ ...billForm, due_date: e.target.value })} className="rounded-sm mt-1" data-testid="bill-due-date" /></div>
                  <div><Label className="text-xs uppercase">PO Reference</Label><Input value={billForm.po_reference} onChange={(e) => setBillForm({ ...billForm, po_reference: e.target.value })} className="rounded-sm mt-1" data-testid="bill-po-ref" /></div>
                  <div>
                    <Label className="text-xs uppercase">GST Mode</Label>
                    <Select value={billForm.gst_mode} onValueChange={(v) => setBillForm({ ...billForm, gst_mode: v })}>
                      <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="exclusive">Exclusive</SelectItem><SelectItem value="inclusive">Inclusive</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-3">
                  {summary.sections.map((sec, si) => (
                    <div key={`${sec.name}-${si}`} className="border border-border rounded-sm p-3">
                      <p className="font-semibold text-sm text-secondary mb-2">{sec.name}</p>
                      <div className="space-y-1.5">
                        {sec.items.map((it, ii) => (
                          <div key={it.item_id || ii} className="grid grid-cols-5 gap-2 items-center text-xs" data-testid={`bill-item-row-${si}-${ii}`}>
                            <span className="col-span-2 truncate">{it.name}</span>
                            <span className="text-muted-foreground">Quoted: {it.qty}</span>
                            <span className="text-muted-foreground">Billed: {it.previously_billed_qty} · Left: {it.remaining_qty}</span>
                            <Input type="number" value={billedQty[`${si}:${ii}`] ?? ""} onChange={(e) => setBilledQty({ ...billedQty, [`${si}:${ii}`]: e.target.value })} className="rounded-sm h-7" data-testid={`bill-item-qty-${si}-${ii}`} />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                <div>
                  <Label className="text-xs uppercase">Override Reason (only needed if billing above remaining qty)</Label>
                  <Input value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} placeholder="e.g. Client approved extra units on site" className="rounded-sm mt-1" data-testid="bill-override-reason" />
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button onClick={createBill} disabled={saving || !summary} className="rounded-sm bg-primary hover:bg-primary/90" data-testid="create-bill-submit-btn">{saving ? "Creating…" : "Create Bill"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!payTarget} onOpenChange={(o) => !o && setPayTarget(null)}>
        <DialogContent data-testid="record-payment-dialog">
          <DialogHeader><DialogTitle className="font-heading">Record Payment</DialogTitle><DialogDescription>{payTarget?.invoice_number} — Balance: ₹{((payTarget?.grand_total || 0) - (payTarget?.paid_amount || 0)).toLocaleString("en-IN")}</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs uppercase">Amount</Label><Input type="number" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} className="rounded-sm mt-1" data-testid="payment-amount-input" /></div>
            <div>
              <Label className="text-xs uppercase">Method</Label>
              <Select value={payForm.method} onValueChange={(v) => setPayForm({ ...payForm, method: v })}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="payment-method-select"><SelectValue /></SelectTrigger>
                <SelectContent>{METHODS.map((m) => <SelectItem key={m} value={m} className="capitalize">{m.replace("_", " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs uppercase">Note</Label><Textarea value={payForm.note} onChange={(e) => setPayForm({ ...payForm, note: e.target.value })} className="rounded-sm mt-1" /></div>
          </div>
          <DialogFooter><Button onClick={recordPayment} className="rounded-sm bg-primary hover:bg-primary/90" data-testid="submit-payment-btn">Record Payment</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!historyTarget} onOpenChange={(o) => !o && setHistoryTarget(null)}>
        <DialogContent data-testid="payment-history-dialog">
          <DialogHeader><DialogTitle className="font-heading">Payment History</DialogTitle><DialogDescription>{historyTarget?.invoice_number}</DialogDescription></DialogHeader>
          <div className="space-y-2 text-sm">
            {historyRows.map((p) => (
              <div key={p.id} className="flex items-center justify-between border-b border-dashed border-border pb-2">
                <div>
                  <p className="font-medium">₹{p.amount.toLocaleString("en-IN")} <span className="text-xs text-muted-foreground capitalize">({p.method.replace("_", " ")})</span></p>
                  <p className="text-xs text-muted-foreground">{(p.paid_at || "").slice(0, 10)} · {p.receipt_number}</p>
                </div>
                <Button size="sm" variant="ghost" onClick={() => downloadReceipt(p.id)} data-testid={`download-receipt-${p.id}`}><Download size={14} /></Button>
              </div>
            ))}
            {historyRows.length === 0 && <p className="text-muted-foreground text-xs">No payments recorded yet.</p>}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!cancelTarget} onOpenChange={(o) => !o && setCancelTarget(null)}>
        <AlertDialogContent data-testid="cancel-bill-dialog">
          <AlertDialogHeader><AlertDialogTitle>Cancel this invoice?</AlertDialogTitle><AlertDialogDescription>Its billed quantity will be restored as unbilled on the quotation. This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmCancel} className="bg-red-600 hover:bg-red-700" data-testid="confirm-cancel-bill">Cancel Invoice</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
